//Libs
import { useMutation, useQuery, useQueryClient } from "react-query";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { Fragment, useEffect, useState } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Column, Workbook } from "exceljs";
import { saveAs } from "file-saver";

//Local
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { BASE_APP_URL } from "../../urls";
import { DbRead, DbWrite } from "../../database";
import * as strings from "../../strings";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import WorkRecordTableColumnsDialog from "../../components/WorkRecordTableColumnsDialog";
import { getWRListTableCustomizedColumns } from "../../assets/js/getWRListTableCustomizedColumns";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { StyledTooltip } from "../../components/StyledTooltip";
import { useAuthStore } from "../../store/firebase-auth";
import { formatWRDataForExcelExport } from "../../assets/js/formatWRDataForExcelExport";
import WorkRecordListPage from "./WorkRecordListPage";
import { CraftTypes } from "../../models/craft-types";
import { ExistingCraftRecord } from "../../models/craft-record";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";
import { logger as devLogger } from "../../logging";
import ConfirmRestoreWorkRecordDialog from "../../components/ConfirmRestoreWorkRecordDialog";

interface Props {
  siteKey: string;
}

export default function DeletedWorkRecordListContainer({ siteKey }: Props) {
  const [getLocationTitle, locationStoreIsLoading, siteKeyLocationList] =
    useSiteKeyLocationsStore((state) => [
      state.getLocationTitle,
      state.loading,
      state.siteKeyLocationList,
    ]);
  const [userDisplayNamesMap, userDisplayNamesMapIsLoading] =
    useUserDisplayNamesStore((state) => [
      state.userDisplayNames,
      state.loading,
    ]);

  const [siteKeyDoc, siteKeyDocIsLoading] = useSiteKeyDocStore((state) => [
    state.siteKeyDoc,
    state.loading,
  ]);

  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const addMessage = useToastMessageStore((state) => state.addToastMessage);
  const queryClient = useQueryClient();

  /* QUERIES */
  /**
   * Query to get the list of work record
   */
  const deletedWorkRecordListQueryKey = [
    "deletedWorkRecordListContainer_deletedWorkRecordList",
    siteKey,
  ];
  const {
    data: deletedWorkRecordList = [],
    isLoading: deletedWorkRecordListIsLoading,
  } = useQuery(deletedWorkRecordListQueryKey, () =>
    DbRead.parentRecords.getAllDeleted(siteKey),
  );

  /**
   * Query to get the list of vehicles
   */
  const vehicleListQueryKey = [
    "deletedWorkRecordListContainer_vehicle",
    siteKey,
  ];
  const { data: vehicleList = [], isLoading: vehicleListIsLoading } = useQuery(
    vehicleListQueryKey,
    () => DbRead.vehicles.getAll(siteKey),
  );

  /* MUTATIONS */
  const mutateRestoreWorkRecord = useMutation(
    async (args: { workRecordRefPath: ExistingCraftRecord["refPath"] }) =>
      await DbWrite.parentRecords.restore(args.workRecordRefPath),
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(deletedWorkRecordList);
      },
    },
  );

  /* STATES & VARIABLES */
  const siteKeyCraftDetails: Record<string, any> =
    siteKeyDoc?.customizations.craftDetails;

  const {
    additionalColumnsSelection,
    additionalColumns,
    workRecordTableColumnsDefaultValues,
  } = getWRListTableCustomizedColumns(siteKeyCraftDetails);

  const localStorageColumnSelection = localStorage.getItem(
    "deletedWorkRecordTableColumnsSelection",
  );

  const [
    workRecordTableColumnsDialogOpen,
    setWorkRecordTableColumnsDialogOpen,
  ] = useState<boolean>(false);
  const [workRecordTableColumnsSelection, setWorkRecordTableColumnsSelection] =
    useState<Record<string, boolean>>(
      localStorageColumnSelection
        ? JSON.parse(localStorageColumnSelection)
        : { ...workRecordTableColumnsDefaultValues, action: true },
    );

  const [confirmRestoreWRDialogOpen, setConfirmRestoreWRDialogOpen] =
    useState<boolean>(false);
  const [workRecordToBeRestored, setWorkRecordToBeRestored] =
    useState<ExistingCraftRecord | null>(null);
  const [isRestoringWRLoading, setIsRestoringWRLoading] =
    useState<boolean>(false);

  /* USE EFFECT */
  useEffect(() => {
    localStorage.setItem(
      "deletedWorkRecordTableColumnsSelection",
      JSON.stringify(workRecordTableColumnsSelection),
    );
  }, [workRecordTableColumnsSelection]);

  /* FUNCTIONS */
  async function handleConfirmRestoreWorkRecord() {
    if (workRecordToBeRestored == null) return;
    setIsRestoringWRLoading(true);

    try {
      if (!workRecordToBeRestored?.timestampRecordCreated) {
        addMessage({
          id: createToastMessageID(),
          message: strings.ERR_TASK_CANNOT_BE_RESTORED,
          dialog: false,
          type: "error",
        });
        return;
      }
      await mutateRestoreWorkRecord.mutateAsync({
        workRecordRefPath: workRecordToBeRestored.refPath,
      });
      addMessage({
        id: createToastMessageID(),
        message: strings.successfulRestore(`${workRecordToBeRestored.title}`),
        dialog: false,
        type: "success",
      });
    } catch (error) {
      devLogger.error(`An error occurred during saveAssignedVehicleIDs`, error);
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    } finally {
      setIsRestoringWRLoading(false);
      setWorkRecordToBeRestored(null);
    }
  }

  function handleRestoreWorkRecord(workRecordDoc: ExistingCraftRecord) {
    setConfirmRestoreWRDialogOpen(true);
    setWorkRecordToBeRestored(workRecordDoc);
  }

  /**
   * Build an xlsx spreadsheet.
   */
  async function saveSpreadSheet() {
    const fileName = strings.DELETED_WORK_RECORDS;

    const columns = [
      {
        key: "title",
        header: "Title",
      },
      {
        key: "description",
        header: "Description",
      },
      {
        key: "numOpenTasks",
        header: "Open Tasks",
      },
      {
        key: "createdBy",
        header: "Created By",
      },
      {
        key: "timestampRecordCreated",
        header: "Created",
      },
    ] as Column[];

    if (vehicleList.length >= 1) {
      const customColumn = {
        key: "assetID",
        header: "Asset",
      } as Column;
      columns.push(customColumn);
    }

    if (siteKeyLocationList.length >= 1) {
      const locationColumn = {
        key: "location",
        header: "Location",
      } as Column;
      columns.push(locationColumn);
    }

    if (siteKeyDoc?.validCraftTypes.includes(CraftTypes.SCAFFOLDING)) {
      const daysStandingColumn = {
        key: "craftDetails.timestampMarkedInstalled",
        header: "# Days Standing",
      } as Column;
      columns.push(daysStandingColumn);
    }

    Object.values(additionalColumns).forEach((columnDetails) => {
      const customColumn = {
        key: columnDetails.field,
        header: columnDetails.headerName,
      } as Column;
      columns.push(customColumn);
    });

    const wb = new Workbook();
    const sheet = wb.addWorksheet(fileName);

    sheet.columns = columns;

    const formattedData = formatWRDataForExcelExport(
      deletedWorkRecordList,
      userDisplayNamesMap,
      getLocationTitle,
      vehicleList,
    );

    sheet.addRows(formattedData);

    // Insert the user's report name as the title row (first row)
    const customTitle = `${
      userDisplayNamesMap[firebaseUser?.uid ?? ""]
    } [${fileName}]`;
    sheet.insertRow(1, [customTitle]);

    // updated the font for first row.
    sheet.getRow(1).font = { bold: true };
    sheet.getRow(2).font = { bold: true };

    try {
      // write the content using writeBuffer
      const buf = await wb.xlsx.writeBuffer();

      // download the processed file
      saveAs(new Blob([buf]), `${customTitle}.xlsx`);
    } catch (error) {
      console.error("Something Went Wrong", error);
    } finally {
      // removing worksheet's instance to create new one
      wb.removeWorksheet(fileName);
    }
  }

  const actionButtons = (
    <div className="flex gap-4">
      <StyledTooltip title={`Export to Excel`}>
        <button
          onClick={saveSpreadSheet}
          className="rounded-md bg-gray-200 p-2"
        >
          <FileDownloadIcon fontSize="medium" className="shrink-0" />
        </button>
      </StyledTooltip>
      <StyledTooltip title={`Add Additional Columns`}>
        <button
          onClick={() => setWorkRecordTableColumnsDialogOpen(true)}
          className="rounded-md bg-gray-200 p-2"
        >
          <ViewColumnIcon fontSize="medium" className="shrink-0" />
        </button>
      </StyledTooltip>
    </div>
  );

  const workRecordTableColumnsDialog = (
    <WorkRecordTableColumnsDialog
      isOpen={workRecordTableColumnsDialogOpen}
      onClose={() => setWorkRecordTableColumnsDialogOpen(false)}
      setWorkRecordTableColumnsSelection={setWorkRecordTableColumnsSelection}
      getLocationTitle={getLocationTitle}
      additionalColumnsSelection={additionalColumnsSelection}
      workRecordTableColumnsSelection={workRecordTableColumnsSelection}
      vehicleList={vehicleList}
      siteKeyLocationList={siteKeyLocationList}
      validCraftTypes={siteKeyDoc?.validCraftTypes}
    />
  );

  const confirmRestoreWRDialog = workRecordToBeRestored && (
    <ConfirmRestoreWorkRecordDialog
      isOpen={confirmRestoreWRDialogOpen}
      onClose={() => setConfirmRestoreWRDialogOpen(false)}
      handleConfirmRestoreWorkRecord={handleConfirmRestoreWorkRecord}
      isSubmitting={isRestoringWRLoading}
      workRecord={workRecordToBeRestored}
    />
  );

  // RENDER LOADING
  if (
    deletedWorkRecordListIsLoading ||
    locationStoreIsLoading ||
    siteKeyDocIsLoading ||
    userDisplayNamesMapIsLoading ||
    vehicleListIsLoading
  ) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  //RENDER CONTENT
  return (
    <Fragment>
      <WorkRecordListPage
        workRecordList={deletedWorkRecordList}
        getLocationTitle={getLocationTitle}
        userDisplayNamesMap={userDisplayNamesMap}
        workRecordTableColumnsSelection={workRecordTableColumnsSelection}
        handleRestoreWorkRecord={handleRestoreWorkRecord}
        vehicleList={vehicleList}
        customColumns={additionalColumns}
        siteKeyLocationList={siteKeyLocationList}
        validCraftTypes={siteKeyDoc?.validCraftTypes}
      >
        {{
          ActionButtons: actionButtons,
          DateRangePicker: null,
        }}
      </WorkRecordListPage>
      {confirmRestoreWRDialog}
      {workRecordTableColumnsDialog}
    </Fragment>
  );
}
